import Menu from './menu';

import Util from '../utils/util';

import { Modules, Packets, Opcodes } from '@kaetram/common/network';

import type Game from '../game';
import type { DiplomacyInfo, GuildSkillKey, GuildSkillLevels, ListInfo, Member } from '@kaetram/common/types/guild';
import type { GuildPacket } from '@kaetram/common/types/messages/outgoing';

interface ListElement extends HTMLElement {
    identifier?: string;
}

export default class Guilds extends Menu {
    // The banner is the banner of the guild that the player is currently in.
    private banner: HTMLElement = document.querySelector('#guilds > .banner')!;

    // The default container and elements for the guilds list, displayed prior to joining a guild.
    private listContainer: HTMLElement = document.querySelector('#guilds-list-container')!;
    private createButton: HTMLButtonElement = document.querySelector('#create-guild')!;

    // Container for creating a new guild.
    private create: HTMLElement = document.querySelector('#guilds-create-container')!;

    private createError: HTMLElement = document.querySelector('#create-error')!;

    private backButton: HTMLElement = document.querySelector('#guild-back')!;
    private createConfirmButton: HTMLButtonElement = document.querySelector('#guild-create')!;

    private bannerColours: HTMLUListElement = document.querySelector('#banner-colours')!;
    private bannerOutlines: HTMLUListElement = document.querySelector('#banner-outline-colours')!;
    private bannerCrests: HTMLUListElement = document.querySelector('#banner-crests')!;

    private nameInput: HTMLInputElement = document.querySelector('#guild-name-input')!;

    // Decorations for the guild interface banner (updated during creation or when guild data is received).
    private bannerColour: Modules.BannerColour = Modules.BannerColour.Grey;
    private bannerOutline: Modules.BannerOutline | '' = '';
    private bannerCrest: Modules.BannerCrests = Modules.BannerCrests.None;

    // The guild information container (if the player is in a guild).
    private infoContainer: HTMLElement = document.querySelector('#guilds-info-container')!;
    private chatLog: HTMLUListElement = document.querySelector('#guild-chat-log')!;
    private chatInput: HTMLInputElement = document.querySelector('#guild-chat-input')!;

    private guildName: HTMLElement = document.querySelector('#guild-name')!;
    private leaveButton: HTMLElement = document.querySelector('#guild-leave')!;
    private inviteButton: HTMLElement = document.querySelector('#guild-invite')!;

    // Guild invite popup, shown when a leader invites us into their guild.
    private inviteModal: HTMLElement = document.querySelector('#guild-invite-modal')!;
    private inviteModalText: HTMLElement = document.querySelector('#guild-invite-modal-text')!;
    private inviteModalAccept: HTMLButtonElement = document.querySelector(
        '#guild-invite-modal-accept'
    )!;
    private inviteModalDecline: HTMLButtonElement = document.querySelector(
        '#guild-invite-modal-decline'
    )!;
    private pendingInviteIdentifier = ''; // Guild identifier from the last invite popup.

    // Guild experience display.
    private experienceDisplay: HTMLElement = document.querySelector('#guild-experience')!;

    // List where we store players/guilds list (depending on the context).
    private guildList: HTMLUListElement = document.querySelector('#guilds-list-container > ul')!;
    private memberList: HTMLUListElement = document.querySelector('#member-list')!;
    private diplomacyList: HTMLUListElement = document.querySelector('#diplomacy-list')!;
    private skillTreeContainer: HTMLElement = document.querySelector('#skill-tree-container')!;
    private sidebarList: HTMLUListElement = document.querySelector('#sidebar-list')!;

    // Indexing - default values, used for pagination.
    private from = 0;
    private to = 10;

    // Sidebar we're currently naivgating.
    private currentSidebar:
        | 'sidebar-members'
        | 'sidebar-chat'
        | 'sidebar-diplomacy'
        | 'sidebar-skills' = 'sidebar-members';

    // Cached diplomacy data for the UI.
    private alliances: DiplomacyInfo[] = [];
    private hostiles: DiplomacyInfo[] = [];
    private allianceRequests: DiplomacyInfo[] = [];

    // Cached skill tree data.
    private guildLevel = 0;
    private guildSkillPoints = 0;
    private guildSkills: GuildSkillLevels = {};
    private isOwner = false;

    public constructor(private game: Game) {
        super('#guilds', '#close-guilds', '#guilds-button');

        this.createButton.addEventListener('click', this.handleCreate.bind(this));

        this.backButton.addEventListener('click', this.handleBackButton.bind(this));
        this.createConfirmButton.addEventListener('click', this.handleCreateConfirm.bind(this));
        this.leaveButton.addEventListener('click', this.handleLeave.bind(this));
        this.inviteButton.addEventListener('click', this.handleInviteClick.bind(this));

        // Guild invite popup buttons.
        this.inviteModalAccept.addEventListener('click', () => this.acceptInvite());
        this.inviteModalDecline.addEventListener('click', () => this.hideInvitePopup());

        this.loadSidebar();
        this.loadDecorations();
    }

    /**
     * Updates the user interface according to the action that was performed.
     * @param opcode The opcode of the action that was performed.
     * @param info Information about the opcode we received.
     */

    public handle(opcode: Opcodes.Guild, info: GuildPacket): void {
        switch (opcode) {
            case Opcodes.Guild.Join: {
                return this.handleMemberJoin(info.username!, info.serverId!);
            }

            case Opcodes.Guild.Login: {
                return this.handleConnect(info);
            }

            case Opcodes.Guild.Leave: {
                if (info?.username) return this.handleMemberLeave(info.username);

                return this.handleBackButton();
            }

            case Opcodes.Guild.Rank: {
                break;
            }

            case Opcodes.Guild.Update: {
                return this.loadMembers(info.members!);
            }

            case Opcodes.Guild.Experience: {
                break;
            }

            case Opcodes.Guild.List: {
                return this.loadList(info.guilds, info.total);
            }

            case Opcodes.Guild.Error: {
                return this.setError(info.message);
            }

            case Opcodes.Guild.Chat: {
                return this.handleChat(info);
            }

            case Opcodes.Guild.Invite: {
                return this.showInvitePopup(info);
            }

            case Opcodes.Guild.DiplomacyList: {
                return this.handleDiplomacyList(info);
            }

            case Opcodes.Guild.AllianceRequest: {
                return this.handleAllianceRequest(info);
            }

            case Opcodes.Guild.AllianceAccept:
            case Opcodes.Guild.AllianceDecline:
            case Opcodes.Guild.AllianceBreak:
            case Opcodes.Guild.HostileDeclare:
            case Opcodes.Guild.HostileRemove: {
                this.game.socket.send(Packets.Guild, {
                    opcode: Opcodes.Guild.DiplomacyList
                });
                return;
            }

            case Opcodes.Guild.SkillTree: {
                return this.handleSkillTree(info);
            }

            case Opcodes.Guild.SkillUpgrade: {
                return this.handleSkillUpgrade(info);
            }
        }
    }

    /**
     * Handler for when the player clicks the create guild button.
     */

    private handleCreate(): void {
        // Hide the default information and show the create guild form.
        this.listContainer.style.display = 'none';
        this.infoContainer.style.display = 'none';

        // Display the create guild form.
        this.create.style.display = 'block';
    }

    /**
     * Handler for when the player wants to go back to the guilds list.
     * Basically the opposite of `handleCreate`.
     */

    private handleBackButton(): void {
        // Reset the banner decorations and update it.
        this.bannerColour = Modules.BannerColour.Grey;
        this.bannerOutline = '';
        this.bannerCrest = Modules.BannerCrests.None;

        this.updateBanner();
        this.cleanSelectedCrests(true);

        // Clear all the errors.
        this.setError();

        // Empty the members list.
        this.memberList.innerHTML = '';

        // Empty the chat log.
        this.chatLog.innerHTML = '';

        // Empty the diplomacy list.
        this.diplomacyList.innerHTML = '';
        this.diplomacyList.style.display = 'none';

        // Empty the skill tree.
        this.skillTreeContainer.innerHTML = '';
        this.skillTreeContainer.style.display = 'none';

        // Hide other containers and show the default information.
        this.create.style.display = 'none';
        this.infoContainer.style.display = 'none';

        // Display the default information.
        this.listContainer.style.display = 'block';

        // Request the guilds list from the server.
        this.requestList();
    }

    /**
     * Handler for when we click the create confirm button.
     * This will send the packet to the server with the information provided.
     */

    private handleCreateConfirm(): void {
        // Clear the error message.
        this.setError();

        // Verify the values client-sided (server also verifies them).
        if (this.bannerOutline === '')
            return this.setError('Por favor selecciona un color de borde.');

        // Ensure the guild name is valid.
        if (this.nameInput.value.length < 3 || this.nameInput.value.length > 16)
            return this.setError('El nombre del gremio debe tener entre 3 y 15 caracteres.');

        // Send the packet to the server with the information.
        this.game.socket.send(Packets.Guild, {
            opcode: Opcodes.Guild.Create,
            name: this.nameInput.value,
            colour: this.bannerColour,
            outline: this.bannerOutline,
            crest: this.bannerCrest
        });
    }

    /**
     * Handler for when the player clicks the leave guild button.
     */

    private handleLeave(): void {
        this.game.socket.send(Packets.Guild, {
            opcode: Opcodes.Guild.Leave
        });
    }

    /**
     * Prompts the leader for a username and sends a guild invitation to them.
     * The target receives an accept/decline popup on their client.
     */

    private handleInviteClick(): void {
        let username = prompt('Nombre del jugador a invitar al gremio:');
        if (!username?.trim()) return;

        this.game.socket.send(Packets.Guild, {
            opcode: Opcodes.Guild.Invite,
            username: username.trim()
        });
    }

    /**
     * Shows the guild invite popup (accept/decline) when a leader invites us.
     * @param info Contains the inviter's username, the guild name, and the
     * guild identifier used to join if we accept.
     */

    private showInvitePopup(info: GuildPacket): void {
        if (!this.inviteModal) return;

        this.pendingInviteIdentifier = info.identifier || '';
        this.inviteModalText.textContent = `${info.username || 'Alguien'} te invitó al gremio "${
            info.name || ''
        }".`;
        this.inviteModal.hidden = false;
    }

    /**
     * Accepts the pending guild invite by joining the stored guild identifier.
     */

    private acceptInvite(): void {
        if (this.pendingInviteIdentifier)
            this.game.socket.send(Packets.Guild, {
                opcode: Opcodes.Guild.Join,
                identifier: this.pendingInviteIdentifier
            });

        this.hideInvitePopup();
    }

    /**
     * Hides the guild invite popup and clears the stored identifier.
     */

    private hideInvitePopup(): void {
        if (this.inviteModal) this.inviteModal.hidden = true;
        this.pendingInviteIdentifier = '';
    }

    /**
     * Handles connection received from the server. We essentially
     * clear all the other interfaces and focus on the guild interface.
     * The player object contains all the guild information necessary.
     * @param info Contains information about the guild, such as decorations.
     */

    private handleConnect(info: GuildPacket): void {
        // Clear the error message.
        this.setError();

        // Hide the default information.
        this.listContainer.style.display = 'none';

        // Hide the create guild form just in case.
        this.create.style.display = 'none';

        // Display the guild information container.
        this.infoContainer.style.display = 'block';

        // Load the guild decorations.
        this.bannerColour = info.decoration?.banner || Modules.BannerColour.Grey;
        this.bannerOutline = info.decoration?.outline || '';
        this.bannerCrest = info.decoration?.crest;

        this.updateBanner();

        // Clear the guild name and members list.
        this.guildName.innerHTML = '';
        this.memberList.innerHTML = '';

        // Update the guild name
        let name = document.createElement('p');

        name.className = 'stroke';

        name.innerHTML = info.name || 'Desconocido';

        this.guildName.append(name);

        // Update guild experience display.
        let exp = info.experience ?? 0,
            lvl = info.level ?? 0,
            nextLvl = (lvl + 1) * 200;
        this.experienceDisplay.innerHTML = `XP del Gremio: ${exp}/${nextLvl} — Nivel ${lvl}`;

        // Cache skill tree data from login.
        this.guildLevel = info.level ?? 0;
        this.guildSkillPoints = info.skillPoints ?? 0;
        this.guildSkills = info.skills ?? {};

        // Update the leave button to disband if we're the leader, and only
        // expose the invite button to the leader.
        let amLeader = this.game.player.name.toLowerCase() === info.owner;
        this.isOwner = amLeader;
        if (amLeader) this.leaveButton.innerHTML = 'Disolver';
        this.inviteButton.hidden = !amLeader;

        // Update the guild members list.
        for (let member of info.members!)
            this.createElement(this.memberList, member.rank!, member.username);
    }

    /**
     * Creates a new element based on the username is that joining. Since the player
     * is brand new, then the default rank is the lowest rank. Other inforamtion
     * is not really necessary.
     * @param username The username of the member that joined the guild.
     * @param serverId The server id of the member that joined the guild.
     */

    private handleMemberJoin(username: string, serverId = -1): void {
        // Ignore if we're the one joining.
        if (username === this.game.player.name.toLowerCase()) return;

        this.createElement(this.memberList, Modules.GuildRank.Fledgling, username);

        this.loadMembers([{ username, serverId }]);
    }

    /**
     * Removes a child element from the member list based on the username.
     * @param username The username of the member that left the guild.
     */

    private handleMemberLeave(username: string): void {
        let element = this.getElement(this.memberList, username);

        element?.remove();
    }

    /**
     * Handles clicking on a side bar in a guild interface and updating
     * the interface accordingly.
     * @param menu The string identification of the menu we want to open.
     */

    private handleSidebar(
        menu: 'sidebar-members' | 'sidebar-chat' | 'sidebar-diplomacy' | 'sidebar-skills'
    ): void {
        // Ignore if we don't have a guild.
        if (!this.game.player.guild) return;

        // Ignore if we're already on the menu.
        if (this.currentSidebar === menu) return;

        // Hide everything first.
        this.memberList.style.display = 'none';
        this.chatLog.style.display = 'none';
        this.chatInput.style.display = 'none';
        this.diplomacyList.style.display = 'none';
        this.skillTreeContainer.style.display = 'none';
        this.infoContainer.className = '';

        switch (menu) {
            case 'sidebar-members': {
                this.memberList.style.display = 'block';
                break;
            }

            case 'sidebar-chat': {
                this.infoContainer.className = 'guild-chat';
                this.chatInput.style.display = 'block';
                this.chatLog.style.display = 'block';
                break;
            }

            case 'sidebar-diplomacy': {
                this.diplomacyList.style.display = 'block';
                this.game.socket.send(Packets.Guild, {
                    opcode: Opcodes.Guild.DiplomacyList
                });
                break;
            }

            case 'sidebar-skills': {
                this.skillTreeContainer.style.display = 'block';
                this.game.socket.send(Packets.Guild, {
                    opcode: Opcodes.Guild.SkillTree
                });
                break;
            }
        }

        // Update the current sidebar.
        this.currentSidebar = menu;
    }

    /**
     * Handles a message received from the server. We add the message
     * to the chat log alongside information about who and from
     * where the message was sent.
     * @param packet Contains information about the message.
     */

    private handleChat(packet: GuildPacket): void {
        // Ignore invalid packets (shouldn't happen).
        if (!packet.username || !packet.serverId) return;

        // Format the source of the message.
        let source = `[W${packet.serverId}] ${Util.formatName(packet.username)}`,
            element = document.createElement('p');

        // Add the message to the chat log.
        element.innerHTML = `${source} » ${packet.message}`;

        this.chatLog.append(element);
    }

    /**
     * Handles the keydown event for the guild interface. Usually when the
     * player is using the chat input, we want to re-route the enter key to
     * send the message to the server.
     * @param key The key that was pressed.
     */

    public keyDown(key: string): void {
        // Ignore if we're not on the chat sidebar.
        if (key !== 'Enter' || this.currentSidebar !== 'sidebar-chat') return;

        // Ignore if the input is empty.
        if (this.chatInput.value.length === 0) return;

        // Send the message to the server.
        this.sendChat();
    }

    /**
     * Requests a list of guilds from the server.
     */

    private requestList(): void {
        if (this.game.player.guild) return;

        return this.game.socket.send(Packets.Guild, {
            opcode: Opcodes.Guild.List,
            from: this.from,
            to: this.to
        });
    }

    /**
     * Sends a chat message to the server.
     */

    private sendChat(): void {
        // Ignore if the input is empty.
        if (this.chatInput.value.length === 0) return;

        // Send the message to the server.
        this.game.socket.send(Packets.Guild, {
            opcode: Opcodes.Guild.Chat,
            message: this.chatInput.value
        });

        // Clear the input.
        this.chatInput.value = '';
    }

    /**
     * Loads a list of guilds received from the server.
     * @param guilds Contains information about each guild.
     * @param total The total number of guilds available (used for pagination).
     */

    private loadList(guilds: ListInfo[] = [], total = 0): void {
        // Nothing to do if there are no guilds.
        if (total === 0) return;

        // Clear the list of guilds.
        this.guildList.innerHTML = '';

        // Remove the description for no guilds available.
        let description = this.listContainer.querySelector('#guilds-info')!;

        description.innerHTML = '';

        // Iterate through the guilds and create a list element for each one.
        for (let guild of guilds)
            this.createElement(this.guildList, 'guild', guild.name, guild.members);
    }

    /**
     * Creates an event listener and action for each sidebar element.
     */

    private loadSidebar(): void {
        for (let child of this.sidebarList.children)
            child.addEventListener('click', () => {
                // Deselect all the other elements.
                this.cleanSidebar();

                // Select the current element.
                child.classList.add('active');

                // Handle updating the interface.
                this.handleSidebar(
                    child.id as
                        | 'sidebar-members'
                        | 'sidebar-chat'
                        | 'sidebar-diplomacy'
                        | 'sidebar-skills'
                );
            });
    }

    /**
     * Programmatically creates the banner decorations for the guild interface. We create
     * each individual element on the page and assign an event listener to each one. We
     * do this to make it easier ot add new decorations in the future.
     */

    private loadDecorations(): void {
        this.loadColours();
        this.loadOutlines();
        this.loadCrests();
    }

    /**
     * Loads the banner colour elements for the create guild interface. We
     * do it programmatically to make it easier to add new colours in the future.
     */

    private loadColours(): void {
        // Iterate through the banner colours and create a list element for each one.
        for (let colour in Modules.BannerColour) {
            let element = document.createElement('li'),
                colourName = Modules.BannerColour[colour as keyof typeof Modules.BannerColour];

            // Add the banner colour classes to the element.
            element.className = `banner-colour-button banner-colour-button-${colourName}`;

            // Event listener to change the selected colour.
            element.addEventListener('click', () => {
                // Update the banner colour.
                this.bannerColour = colourName;

                // Update the banner.
                this.updateBanner();
            });

            // Add the element to the list of banner colours.
            this.bannerColours.append(element);
        }
    }

    /**
     * Loads the banner outline elements for the create guild interface. We
     * do it programmatically to make it easier to add new outlines in the future.
     */

    private loadOutlines(): void {
        // Iterate through the banner outlines and create a list element for each one.
        for (let colour in Modules.BannerOutline) {
            let element = document.createElement('li'),
                outline = Modules.BannerOutline[colour as keyof typeof Modules.BannerOutline];

            // Add the banner outline classes to the element.
            element.className = `banner-outline-button banner-outline-button-${outline}`;

            // Event listener to change the selected outline.
            element.addEventListener('click', () => {
                // Update the banner outline.
                this.bannerOutline = outline;

                // Update the banner.
                this.updateBanner();
            });

            // Add the element to the list of banner outlines.
            this.bannerOutlines.append(element);
        }
    }

    /**
     * Loads the banner crest elements for the create guild interface. We create
     * the appropriate event listener for each crest and add it to the list.
     */

    private loadCrests(): void {
        // Add the crest elements to the banner.
        for (let crest in Modules.BannerCrests) {
            let element = document.createElement('li'),
                icon = document.createElement('div'),
                crestName = Modules.BannerCrests[crest as keyof typeof Modules.BannerCrests];

            // Add the class to the element.
            element.className = `crest-element`;

            // Add the icon class to the icon element.
            icon.className = `banner-crest-icon banner-crest-icon-${crestName}`;

            // Add the icon to the element.
            element.append(icon);

            // Set the default empty crest icon as active.
            if (crestName === 'none') element.classList.add('active');

            // Event listener to change the selected crest.
            element.addEventListener('click', () => {
                this.cleanSelectedCrests();

                // Update the banner crest.
                this.bannerCrest = crestName;

                // Update the banner.
                this.updateBanner();

                // Set active crest.
                element.classList.add('active');
            });

            // Add the element to the list of crests.
            this.bannerCrests.append(element);
        }
    }

    /**
     * Loads a list of members (specifically their online status) for the guild interface.
     * @param members Contains an array of guild members and their online status.
     */

    private loadMembers(members: Member[]): void {
        for (let member of members) {
            let element = this.getElement(this.memberList, member.username);

            if (!element) continue;

            let nameElement = element.querySelector('.name')!,
                serverElement = element.querySelector('.server')!,
                colour =
                    member.serverId === -1
                        ? 'red'
                        : member.serverId === this.game.player.serverId
                        ? 'green'
                        : 'yellow';

            // Update the colour based on the online status.
            nameElement.className = `name`;
            serverElement.className = `server ${colour}`;

            serverElement.innerHTML =
                member.serverId === -1 ? 'Desconectado' : `Mundo ${member.serverId}`;
        }
    }

    /**
     * Override for the show function where we send a packet requesting
     * a list of active guilds if the player is not in a guild.
     */

    public override show(): void {
        super.show();

        // If we're already in a guild, show our own panel instead of the
        // guild list — otherwise a stale list (with "Unirse" buttons) could
        // remain visible even though we can't join another guild.
        if (this.game.player.guild) {
            this.create.style.display = 'none';
            this.listContainer.style.display = 'none';
            this.infoContainer.style.display = 'block';
            return;
        }

        // No guild so we request a list of active guilds.
        this.requestList();
    }

    /**
     * Override for the hide function where we reset the pagination.
     * We also make sure we hide the create interface if the player
     * does not have a guild.
     */

    public override hide(): void {
        super.hide();

        // Reset the pagination.
        this.from = 0;
        this.to = 10;

        // Hide the create interface if the player is not in a guild.
        if (!this.game.player.guild) this.handleBackButton();
    }

    /**
     * Creates an element and appends it to the list. The element depends on the type
     * we are provided. Generally we have two types: guilds and ranks. When creating a rank
     * based element it's generally for players, whereas a guild one is for a list of
     * available guilds.
     * @param list The list to append the element to.
     * @param type The type of element we are creating.
     * @param name The name to display in the element.
     */

    private createElement(
        list: HTMLUListElement,
        type: Modules.GuildRank | 'guild',
        name: string,
        count = 0
    ): void {
        let element = document.createElement('li') as ListElement,
            nameElement = document.createElement('span'),
            isGuild = type === 'guild',
            slotType = type === 'guild' ? 'guild' : Modules.GuildRank[type].toLowerCase();

        // Assign the name as the identifier for the element
        element.identifier = name;

        // Add the classes to the element and name element.
        element.className = `slot-element slot-${slotType} stroke`;
        nameElement.className = `name`;

        // Set the name of the element, format it if it's a player name.
        nameElement.innerHTML = isGuild ? name : Util.formatName(name, 14);

        // Conditional for dealing with guild element creation.
        if (isGuild) {
            if (count > 0) {
                let countElement = document.createElement('span');

                // Add the class and inner HTML to the count element.
                countElement.classList.add('count');
                countElement.innerHTML = `${count}/${Modules.Constants.MAX_GUILD_MEMBERS}`;

                element.append(countElement);
            }

            // Only offer the "Unirse" button if we're not already in a guild.
            // The server rejects the join anyway, but the button shouldn't even
            // show for a player who already belongs to a guild.
            if (this.game.player.guild) element.append(nameElement);
            else {
                // Create the join button for the guild.
                let joinButton = document.createElement('div');

                // Add the class to the join button.
                joinButton.className = 'element-button stroke';

                // Add the inner HTML to the join button.
                joinButton.innerHTML = 'Unirse';

                // Add the event listener to the join button.
                joinButton.addEventListener('click', () => {
                    this.game.socket.send(Packets.Guild, {
                        opcode: Opcodes.Guild.Join,
                        identifier: name.toLowerCase()
                    });
                });

                // Append name and join elements.
                element.append(nameElement, joinButton);
            }
        }

        // Append just the name element.
        if (!isGuild) {
            element.append(nameElement);

            let serverElement = document.createElement('span'),
                isPlayer = this.game.player.name.toLowerCase() === element.identifier;

            serverElement.className = `server ${isPlayer ? 'green' : 'red'}`;

            serverElement.innerHTML = isPlayer ? 'En línea' : 'Desconectado';

            element.append(serverElement);
        }

        // Append the element to the list.
        list.append(element);
    }

    /**
     * Removes the active class from all the crest selection elements.
     */

    private cleanSelectedCrests(selectEmpty = false): void {
        for (let crest of this.bannerCrests.children) crest.classList.remove('active');

        // Select the empty crest if specified.
        if (selectEmpty) this.bannerCrests.children[0].classList.add('active');
    }

    /**
     * Deselects all the sidebar elements by removing the active class.
     */

    private cleanSidebar(): void {
        for (let element of this.sidebarList.children) element.classList.remove('active');
    }

    /**
     * Updates the banner with the specified colours (in the class).
     */

    private updateBanner(): void {
        let outlineElement = this.banner.querySelector('.banner-outline')!,
            crestElement = this.banner.querySelector('.banner-crest')!;

        // Update the classes with the new colours.
        this.banner.className = `banner banner-${this.bannerColour}`;

        // Update the outline and crest if specified
        outlineElement.className = `banner-outline${
            this.bannerOutline ? ` banner-outline-${this.bannerOutline}` : ''
        }`;

        if (this.bannerCrest)
            crestElement.className = `banner-crest banner-crest-${this.bannerCrest}`;
    }

    /**
     * Grabs a list element from the list based on the identifier.
     * @param list The list we are searching through.
     * @param identifier The identifier we are searching for.
     * @returns A list element if found otherwise undefined.
     */

    private getElement(list: HTMLUListElement, identifier: string): ListElement | undefined {
        for (let element of list.children)
            if ((element as ListElement).identifier === identifier) return element as ListElement;

        return undefined;
    }

    /**
     * Updates the create error element with a message.
     * @param text The message to display.
     */

    private handleDiplomacyList(info: GuildPacket): void {
        this.alliances = info.alliances || [];
        this.hostiles = info.hostiles || [];
        this.allianceRequests = info.allianceRequests || [];

        this.renderDiplomacy();
    }

    private handleAllianceRequest(info: GuildPacket): void {
        if (!info.targetGuildName) return;

        let accept = confirm(`El gremio "${info.targetGuildName}" quiere una alianza. ¿Aceptar?`);

        this.game.socket.send(Packets.Guild, {
            opcode: accept ? Opcodes.Guild.AllianceAccept : Opcodes.Guild.AllianceDecline,
            targetGuild: info.targetGuild
        });
    }

    private renderDiplomacy(): void {
        this.diplomacyList.innerHTML = '';

        let isOwner = this.game.player.name.toLowerCase() === this.game.player.guild?.owner;

        // Alliance requests section.
        if (this.allianceRequests.length > 0) {
            let header = document.createElement('p');
            header.className = 'stroke';
            header.style.color = '#ffcc00';
            header.innerHTML = '— Solicitudes —';
            this.diplomacyList.append(header);

            for (let req of this.allianceRequests) {
                let row = document.createElement('li');
                row.className = 'slot-element stroke';

                let name = document.createElement('span');
                name.className = 'name';
                name.innerHTML = req.name;
                name.style.color = '#ffcc00';
                row.append(name);

                if (isOwner) {
                    let acceptBtn = document.createElement('div');
                    acceptBtn.className = 'element-button stroke';
                    acceptBtn.innerHTML = 'OK';
                    acceptBtn.addEventListener('click', () => {
                        this.game.socket.send(Packets.Guild, {
                            opcode: Opcodes.Guild.AllianceAccept,
                            targetGuild: req.identifier
                        });
                    });

                    let declineBtn = document.createElement('div');
                    declineBtn.className = 'element-button element-button-red stroke';
                    declineBtn.innerHTML = 'No';
                    declineBtn.addEventListener('click', () => {
                        this.game.socket.send(Packets.Guild, {
                            opcode: Opcodes.Guild.AllianceDecline,
                            targetGuild: req.identifier
                        });
                    });

                    row.append(acceptBtn, declineBtn);
                }

                this.diplomacyList.append(row);
            }
        }

        // Alliances section.
        if (this.alliances.length > 0) {
            let header = document.createElement('p');
            header.className = 'stroke';
            header.style.color = '#64ff64';
            header.innerHTML = '— Alianzas —';
            this.diplomacyList.append(header);

            for (let ally of this.alliances) {
                let row = document.createElement('li');
                row.className = 'slot-element stroke';

                let name = document.createElement('span');
                name.className = 'name';
                name.innerHTML = ally.name;
                name.style.color = '#64ff64';
                row.append(name);

                if (isOwner) {
                    let breakBtn = document.createElement('div');
                    breakBtn.className = 'element-button element-button-red stroke';
                    breakBtn.innerHTML = 'X';
                    breakBtn.addEventListener('click', () => {
                        this.game.socket.send(Packets.Guild, {
                            opcode: Opcodes.Guild.AllianceBreak,
                            targetGuild: ally.identifier
                        });
                    });
                    row.append(breakBtn);
                }

                this.diplomacyList.append(row);
            }
        }

        // Hostiles section.
        if (this.hostiles.length > 0) {
            let header = document.createElement('p');
            header.className = 'stroke';
            header.style.color = '#ff5050';
            header.innerHTML = '— Hostiles —';
            this.diplomacyList.append(header);

            for (let hostile of this.hostiles) {
                let row = document.createElement('li');
                row.className = 'slot-element stroke';

                let name = document.createElement('span');
                name.className = 'name';
                name.innerHTML = hostile.name;
                name.style.color = '#ff5050';
                row.append(name);

                if (isOwner) {
                    let removeBtn = document.createElement('div');
                    removeBtn.className = 'element-button stroke';
                    removeBtn.innerHTML = 'X';
                    removeBtn.addEventListener('click', () => {
                        this.game.socket.send(Packets.Guild, {
                            opcode: Opcodes.Guild.HostileRemove,
                            targetGuild: hostile.identifier
                        });
                    });
                    row.append(removeBtn);
                }

                this.diplomacyList.append(row);
            }
        }

        // Empty state.
        if (
            this.alliances.length === 0 &&
            this.hostiles.length === 0 &&
            this.allianceRequests.length === 0
        ) {
            let empty = document.createElement('p');
            empty.className = 'stroke';
            empty.innerHTML = 'Sin diplomacia activa.';
            this.diplomacyList.append(empty);
        }

        // Action buttons for the guild owner.
        if (isOwner) {
            let actions = document.createElement('div');
            actions.style.marginTop = '4px';
            actions.style.textAlign = 'center';

            let allyBtn = document.createElement('div');
            allyBtn.className = 'element-button element-button-large stroke';
            allyBtn.innerHTML = 'Alianza';
            allyBtn.addEventListener('click', () => {
                let target = prompt('Nombre del gremio para solicitar alianza:');
                if (!target?.trim()) return;
                this.game.socket.send(Packets.Guild, {
                    opcode: Opcodes.Guild.AllianceRequest,
                    targetGuild: target.trim().toLowerCase()
                });
            });

            let hostileBtn = document.createElement('div');
            hostileBtn.className = 'element-button-large element-button-red stroke';
            hostileBtn.innerHTML = 'Hostil';
            hostileBtn.addEventListener('click', () => {
                let target = prompt('Nombre del gremio para declarar hostil:');
                if (!target?.trim()) return;
                this.game.socket.send(Packets.Guild, {
                    opcode: Opcodes.Guild.HostileDeclare,
                    targetGuild: target.trim().toLowerCase()
                });
            });

            actions.append(allyBtn, hostileBtn);
            this.diplomacyList.append(actions);
        }
    }

    private setError(text = ''): void {
        this.createError.innerHTML = text;
    }

    // ── Skill Tree ──────────────────────────────────────────────────────

    private static SKILL_DEFS: {
        key: GuildSkillKey;
        name: string;
        description: string;
        icon: string;
        maxLevel: number;
        classRestriction?: string;
        category: string;
    }[] = [
        { key: 'healthRegen', name: 'Regeneración de Salud', description: '+1% regen HP/nivel', icon: 'health_regen.png', maxLevel: 10, category: 'General' },
        { key: 'defenseBoost', name: 'Aumento de Defensa', description: '+2 defensa/nivel', icon: 'defense_boost.png', maxLevel: 10, category: 'General' },
        { key: 'speedBoost', name: 'Velocidad de Movimiento', description: '+1 velocidad/nivel', icon: 'speed_boost.png', maxLevel: 10, category: 'General' },
        { key: 'maxHp', name: 'Aumento de Vida', description: '+10 HP máx/nivel', icon: 'max_hp.png', maxLevel: 10, category: 'General' },
        { key: 'fortressClan', name: 'Fortaleza del Clan', description: '+5 HP máx/nivel', icon: 'fortress_clan.png', maxLevel: 10, category: 'General' },
        { key: 'magicResist', name: 'Resistencia Mágica', description: '-1% daño mágico/nivel', icon: 'magic_resist.png', maxLevel: 10, category: 'General' },
        { key: 'physicalResist', name: 'Resistencia Física', description: '-1% daño físico/nivel', icon: 'physical_resist.png', maxLevel: 10, category: 'General' },
        { key: 'sharedExp', name: 'Experiencia Compartida', description: '+2% XP/nivel', icon: 'shared_exp.png', maxLevel: 10, category: 'General' },
        { key: 'fortune', name: 'Fortuna', description: '+1% Drop Rate/nivel', icon: 'fortune.png', maxLevel: 10, category: 'General' },
        { key: 'clanGold', name: 'Oro del Clan', description: '+2% oro/nivel', icon: 'clan_gold.png', maxLevel: 10, category: 'General' },
        { key: 'expertMining', name: 'Minería Experta', description: '+5% vel. minería/nivel', icon: 'expert_mining.png', maxLevel: 10, category: 'Profesiones' },
        { key: 'lumberjack', name: 'Leñador', description: '+5% vel. tala/nivel', icon: 'lumberjack.png', maxLevel: 10, category: 'Profesiones' },
        { key: 'fisher', name: 'Pescador', description: '+5% peces raros/nivel', icon: 'fisher.png', maxLevel: 10, category: 'Profesiones' },
        { key: 'gatherer', name: 'Recolector', description: '+5% hierbas/nivel', icon: 'gatherer.png', maxLevel: 10, category: 'Profesiones' },
        { key: 'counterStrike', name: 'Contra Golpe', description: '2.5% contraataque/nivel', icon: 'counter_strike.png', maxLevel: 10, classRestriction: 'guerrero', category: 'Guerrero' },
        { key: 'bleed', name: 'Desangrar', description: '0.5% DoT/nivel', icon: 'bleed.png', maxLevel: 10, classRestriction: 'guerrero', category: 'Guerrero' },
        { key: 'steelShield', name: 'Escudo de Acero', description: '1% reducir golpe 20%/nivel', icon: 'steel_shield.png', maxLevel: 10, classRestriction: 'guerrero', category: 'Guerrero' },
        { key: 'swordMastery', name: 'Maestría Espadas', description: '+2 daño espada/nivel', icon: 'sword_mastery.png', maxLevel: 10, classRestriction: 'guerrero', category: 'Guerrero' },
        { key: 'charge', name: 'Embestida', description: '1% empujar enemigo/nivel', icon: 'charge.png', maxLevel: 10, classRestriction: 'guerrero', category: 'Guerrero' },
        { key: 'intimidation', name: 'Intimidación', description: '1% debuff daño/nivel', icon: 'intimidation.png', maxLevel: 10, classRestriction: 'guerrero', category: 'Guerrero' },
        { key: 'guard', name: 'Guardia', description: '0.5% bloquear ataque/nivel', icon: 'guard.png', maxLevel: 10, classRestriction: 'guerrero', category: 'Guerrero' },
        { key: 'doubleShot', name: 'Doble Disparo', description: '2.5% doble disparo/nivel', icon: 'double_shot.png', maxLevel: 10, classRestriction: 'arquero', category: 'Arquero' },
        { key: 'infiniteArrows', name: 'Flechas Infinitas', description: '5% no gastar flecha/nivel', icon: 'infinite_arrows.png', maxLevel: 10, classRestriction: 'arquero', category: 'Arquero' },
        { key: 'preciseShot', name: 'Tiro Preciso', description: '+1% precisión/nivel', icon: 'precise_shot.png', maxLevel: 10, classRestriction: 'arquero', category: 'Arquero' },
        { key: 'piercingArrow', name: 'Flecha Perforante', description: '1% atravesar/nivel', icon: 'piercing_arrow.png', maxLevel: 10, classRestriction: 'arquero', category: 'Arquero' },
        { key: 'criticalShot', name: 'Disparo Crítico', description: '+1% crítico/nivel', icon: 'critical_shot.png', maxLevel: 10, classRestriction: 'arquero', category: 'Arquero' },
        { key: 'sniper', name: 'Francotirador', description: '+1 rango cada 3 niveles', icon: 'sniper.png', maxLevel: 10, classRestriction: 'arquero', category: 'Arquero' },
        { key: 'agility', name: 'Agilidad', description: '+1% vel. ataque/nivel', icon: 'agility.png', maxLevel: 10, classRestriction: 'arquero', category: 'Arquero' },
        { key: 'metamorphosis', name: 'Metamorfosis', description: '1% oveja atacante/nivel', icon: 'metamorphosis.png', maxLevel: 10, classRestriction: 'mago', category: 'Mago' },
        { key: 'electrocute', name: 'Electrocutar', description: '0.5% stun/nivel', icon: 'electrocute.png', maxLevel: 10, classRestriction: 'mago', category: 'Mago' },
        { key: 'infiniteMana', name: 'Mana Infinita', description: '5% no gastar maná/nivel', icon: 'infinite_mana.png', maxLevel: 10, classRestriction: 'mago', category: 'Mago' },
        { key: 'arcaneExplosion', name: 'Explosión Arcana', description: '1% AoE/nivel', icon: 'arcane_explosion.png', maxLevel: 10, classRestriction: 'mago', category: 'Mago' },
        { key: 'freeze', name: 'Congelar', description: '1% ralentizar/nivel', icon: 'freeze.png', maxLevel: 10, classRestriction: 'mago', category: 'Mago' },
        { key: 'magicBarrier', name: 'Barrera Mágica', description: '1% absorber 15%/nivel', icon: 'magic_barrier.png', maxLevel: 10, classRestriction: 'mago', category: 'Mago' },
        { key: 'wisdom', name: 'Sabiduría', description: '-1% cooldown/nivel', icon: 'wisdom.png', maxLevel: 10, classRestriction: 'mago', category: 'Mago' },
        { key: 'clanSpirit', name: 'Espíritu del Clan', description: '+1% daño/aliado cerca', icon: 'clan_spirit.png', maxLevel: 10, category: 'Cooperación' },
        { key: 'aid', name: 'Auxilio', description: 'Revivir con 5%+1% HP/nivel', icon: 'aid.png', maxLevel: 10, category: 'Cooperación' },
        { key: 'secondWind', name: 'Segundo Aire', description: 'Recuperar HP al 20%', icon: 'second_wind.png', maxLevel: 10, category: 'Cooperación' },
        { key: 'clanBlessing', name: 'Bendición del Clan', description: '-1% debuffs/nivel', icon: 'clan_blessing.png', maxLevel: 10, category: 'Cooperación' },
        { key: 'discipline', name: 'Disciplina', description: '-2% CC duración/nivel', icon: 'discipline.png', maxLevel: 10, category: 'Cooperación' },
        { key: 'sharedHealing', name: 'Curación Compartida', description: '1% heal aliados/nivel', icon: 'shared_healing.png', maxLevel: 10, category: 'Cooperación' },
        { key: 'speedAura', name: 'Aura de Velocidad', description: '+1 vel. con 3+ aliados', icon: 'speed_aura.png', maxLevel: 10, category: 'Cooperación' },
        { key: 'protector', name: 'Protector', description: '1% recibir golpe/aliado', icon: 'protector.png', maxLevel: 10, category: 'Cooperación' },
        { key: 'lastEffort', name: 'Último Esfuerzo', description: '+5% daño aliados al morir', icon: 'last_effort.png', maxLevel: 10, category: 'Cooperación' },
        { key: 'hunter', name: 'Cazador', description: '+1% daño PvP/nivel', icon: 'hunter.png', maxLevel: 10, category: 'PvP' },
        { key: 'defender', name: 'Defensor', description: '-1% daño PvP/nivel', icon: 'defender.png', maxLevel: 10, category: 'PvP' },
        { key: 'glory', name: 'Gloria', description: '+5% daño temp. por kill PvP', icon: 'glory.png', maxLevel: 10, category: 'PvP' },
        { key: 'beastHunter', name: 'Cazador de Bestias', description: '+1% daño PvE/nivel', icon: 'beast_hunter.png', maxLevel: 10, category: 'PvE' },
        { key: 'exterminator', name: 'Exterminador', description: '+2% daño jefes/nivel', icon: 'exterminator.png', maxLevel: 10, category: 'PvE' },
        { key: 'survivor', name: 'Superviviente', description: '-1% daño mobs/nivel', icon: 'survivor.png', maxLevel: 10, category: 'PvE' }
    ];

    private handleSkillTree(info: GuildPacket): void {
        this.guildLevel = info.level ?? 0;
        this.guildSkillPoints = info.skillPoints ?? 0;
        this.guildSkills = info.skills ?? {};
        this.renderSkillTree();
    }

    private handleSkillUpgrade(info: GuildPacket): void {
        this.guildSkillPoints = info.skillPoints ?? this.guildSkillPoints;
        this.guildSkills = info.skills ?? this.guildSkills;
        this.renderSkillTree();
    }

    private renderSkillTree(): void {
        this.skillTreeContainer.innerHTML = '';

        let header = document.createElement('div');
        header.className = 'skill-tree-header stroke';
        header.innerHTML =
            `Nivel del Clan: <span class="skill-tree-level">${this.guildLevel}</span> ` +
            `| Puntos: <span class="skill-tree-points">${this.guildSkillPoints}</span>`;
        this.skillTreeContainer.append(header);

        let categories = new Map<string, typeof Guilds.SKILL_DEFS>();
        for (let def of Guilds.SKILL_DEFS) {
            if (!categories.has(def.category)) categories.set(def.category, []);
            categories.get(def.category)!.push(def);
        }

        for (let [category, skills] of categories) {
            let section = document.createElement('div');
            section.className = 'skill-tree-section';

            let title = document.createElement('div');
            title.className = 'skill-tree-category stroke';
            title.innerHTML = category;
            section.append(title);

            let grid = document.createElement('div');
            grid.className = 'skill-tree-grid';

            for (let skill of skills) {
                let level = this.guildSkills[skill.key] || 0,
                    maxed = level >= skill.maxLevel,
                    canUpgrade = this.isOwner && this.guildSkillPoints > 0 && !maxed;

                let card = document.createElement('div');
                card.className = 'skill-card';
                if (level > 0) card.classList.add('skill-active');
                if (maxed) card.classList.add('skill-maxed');
                if (skill.classRestriction) card.classList.add(`skill-class-${skill.classRestriction}`);

                let icon = document.createElement('img');
                icon.src = `/img/interface/guild_skills/${skill.icon}`;
                icon.className = 'skill-icon';
                icon.alt = skill.name;

                let info = document.createElement('div');
                info.className = 'skill-info';

                let nameEl = document.createElement('div');
                nameEl.className = 'skill-name stroke';
                nameEl.innerHTML = skill.name;

                let levelEl = document.createElement('div');
                levelEl.className = 'skill-level stroke';
                levelEl.innerHTML = `${level}/${skill.maxLevel}`;

                let descEl = document.createElement('div');
                descEl.className = 'skill-desc';
                descEl.innerHTML = skill.description;
                if (skill.classRestriction) {
                    let tag = document.createElement('span');
                    tag.className = `skill-class-tag skill-class-tag-${skill.classRestriction}`;
                    tag.innerHTML = ` [${skill.classRestriction}]`;
                    descEl.append(tag);
                }

                info.append(nameEl, levelEl, descEl);
                card.append(icon, info);

                if (canUpgrade) {
                    let btn = document.createElement('div');
                    btn.className = 'skill-upgrade-btn stroke';
                    btn.innerHTML = '+';
                    btn.addEventListener('click', () => {
                        this.game.socket.send(Packets.Guild, {
                            opcode: Opcodes.Guild.SkillUpgrade,
                            skillKey: skill.key
                        });
                    });
                    card.append(btn);
                }

                grid.append(card);
            }

            section.append(grid);
            this.skillTreeContainer.append(section);
        }
    }
}
